import { useState } from 'react';
import { Button } from '../../ui/Button';
import { Modal } from '../../ui/Modal';
import { Text } from '../../ui/Text';
import { token, zVar } from '../../ui/theme';

/*
 * HowToPlay — a bottom-right corner button that opens a modal with a plain,
 * quick-read explanation of Setback (a.k.a. Pitch). Rules are kept general and
 * beginner-friendly rather than exhaustive.
 */

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section style={{ display: 'grid', gap: 6 }}>
    <Text as="h3" variant="label" color={token('color-gold')}>
      {title}
    </Text>
    <div style={{ display: 'grid', gap: 6 }}>{children}</div>
  </section>
);

const P: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text as="p" variant="body" style={{ lineHeight: 1.5 }}>
    {children}
  </Text>
);

export const HowToPlay: React.FC = () => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen(true)}
        style={{
          position: 'absolute',
          right: 24,
          bottom: 24,
          zIndex: zVar('z-hud'),
        }}
        aria-label="How to play"
      >
        How to Play
      </Button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="How to Play Setback (Pitch)"
        width={560}
        actions={
          <Button variant="primary" onClick={() => setOpen(false)}>
            Got it
          </Button>
        }
      >
        <div
          style={{
            display: 'grid',
            gap: 18,
            maxHeight: '65vh',
            overflowY: 'auto',
            paddingRight: 'var(--space-1)',
          }}
        >
          <Section title="Goal">
            <P>
              Setback is a trick-taking card game played in fixed partnerships (partners sit
              opposite each other). The first team to reach the target score — shown as “Win at” in
              the game panel — wins.
            </P>
          </Section>

          <Section title="The Deal">
            <P>Every player is dealt 6 cards. Play and dealing move clockwise around the table.</P>
          </Section>

          <Section title="Bidding">
            <P>
              Starting to the dealer’s left, each player either passes or bids{' '}
              <strong>2, 3, or 4</strong> — how many points their team promises to win this hand.
              Each bid must beat the one before it. The highest bidder wins the contract.
            </P>
          </Section>

          <Section title="Trump">
            <P>
              The bid winner leads the first card. Whatever suit they lead becomes{' '}
              <strong>trump</strong> for the hand. Trump cards beat every other suit.
            </P>
          </Section>

          <Section title="Playing a Trick">
            <P>
              Play follows the suit that was led: you must follow suit if you can, but you may
              always play a trump instead. If you can’t follow suit and have no trump, play any
              card.
            </P>
            <P>
              The highest trump wins the trick; if no trump was played, the highest card of the led
              suit wins. The winner leads the next trick. A hand is six tricks.
            </P>
          </Section>

          <Section title="Scoring (4 points per hand)">
            <P>Each hand awards up to four points, one for each of:</P>
            <ul
              style={{
                margin: 0,
                paddingLeft: 20,
                display: 'grid',
                gap: 'var(--space-1)',
                lineHeight: 1.5,
              }}
            >
              <li>
                <strong>High</strong> — the highest trump in play.
              </li>
              <li>
                <strong>Low</strong> — the lowest trump in play.
              </li>
              <li>
                <strong>Jack</strong> — winning the Jack of trump in a trick.
              </li>
              <li>
                <strong>Game</strong> — the most card points collected ( Ace = 4, King = 3, Queen =
                2, Jack = 1, 10 = 10).
              </li>
            </ul>
            <P>
              If the bidding team wins at least as many points as they bid, they score their bid. If
              they fall short, they are “set” and <strong>lose</strong> that many points instead.
            </P>
          </Section>

          <Section title="Winning">
            <P>
              Hands continue until a team reaches the target score. Watch the game panel for the
              current bid, trump suit, and each team’s score.
            </P>
          </Section>
        </div>
      </Modal>
    </>
  );
};

export default HowToPlay;
